import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import { asignarPosnet, quitarPosnet } from '../services/posnetsChoferesService';
import { registrarAccion, DONDE } from '../../logs/registrarAccion';

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all', textAlign: 'right', ml: 2 }}>
      {value}
    </Typography>
  </Box>
);

const ModalAsignarPosnet = ({ open, chofer, terminalesPDV, choferPorPosnet, onClose, onSuccess }) => {
  const [terminal, setTerminal] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  // Nombre del chofer que tiene la terminal elegida, informado por el backend (409)
  const [duenioConfirmado, setDuenioConfirmado] = useState(null);
  const [confirmandoQuitar, setConfirmandoQuitar] = useState(false);

  useEffect(() => {
    if (open) {
      setTerminal(null);
      setError('');
      setDuenioConfirmado(null);
      setConfirmandoQuitar(false);
    }
  }, [open, chofer]);

  const duenioLocal = terminal ? choferPorPosnet[terminal.id] : null;
  const duenio = duenioConfirmado || (duenioLocal && duenioLocal.id !== chofer?.id ? duenioLocal.nombre_apellido : null);
  const mismaTerminal = terminal && terminal.id === chofer?.posnet;

  const handleClose = () => {
    if (guardando) return;
    onClose();
  };

  const handleTerminalChange = (e, value) => {
    setTerminal(value);
    setError('');
    setDuenioConfirmado(null);
  };

  const asignar = async (e) => {
    e.preventDefault();
    if (!chofer || !terminal || mismaTerminal) return;

    setGuardando(true);
    setError('');
    try {
      await asignarPosnet({
        id_chofer: chofer.id,
        posnet: terminal.id,
        confirmar_traspaso: Boolean(duenio),
      });

      const accion = duenio
        ? `Traspasó la terminal ${terminal.id} de ${duenio} a ${chofer.nombre_apellido}`
        : `Asignó la terminal ${terminal.id} a ${chofer.nombre_apellido}` +
          (chofer.posnet ? ` (antes tenía ${chofer.posnet})` : '');
      registrarAccion(DONDE.POSNETS, accion);

      setGuardando(false);
      onSuccess('La terminal se asignó correctamente');
    } catch (err) {
      setGuardando(false);
      if (err.status === 409) {
        // Otro chofer (no visible en esta lista) ya la tiene: se pide confirmar el traspaso
        setDuenioConfirmado(err.data?.chofer_actual?.nombre_apellido || 'otro chofer');
        return;
      }
      setError(err.message || 'Error al asignar la terminal');
    }
  };

  const quitar = async () => {
    if (!chofer?.posnet) return;

    setGuardando(true);
    setError('');
    try {
      await quitarPosnet(chofer.id);
      registrarAccion(DONDE.POSNETS, `Quitó la terminal ${chofer.posnet} a ${chofer.nombre_apellido}`);
      setGuardando(false);
      onSuccess('Se quitó la terminal al chofer');
    } catch (err) {
      setGuardando(false);
      setError(err.message || 'Error al quitar la terminal');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {chofer?.posnet ? 'Cambiar posnet' : 'Asignar posnet'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={guardando} sx={{ color: 'inherit' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <form onSubmit={asignar}>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2, mb: 3 }}>
            <InfoRow label="Chofer" value={chofer?.nombre_apellido} />
            <Divider sx={{ my: 0.5 }} />
            <InfoRow label="Sucursal" value={chofer?.sucursal || '-'} />
            <Divider sx={{ my: 0.5 }} />
            <InfoRow label="Posnet actual" value={chofer?.posnet || 'Sin asignar'} />
          </Box>

          <Autocomplete
            size="small"
            options={terminalesPDV}
            value={terminal}
            onChange={handleTerminalChange}
            disabled={guardando}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => option?.id || ''}
            renderOption={(props, option) => {
              const asignado = choferPorPosnet[option.id];
              return (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {option.id}
                      {option.external_pos_id ? ` - CAJA: ${option.external_pos_id}` : ''}
                    </Typography>
                    <Typography variant="caption" color={asignado ? 'warning.main' : 'success.main'}>
                      {asignado ? `Asignada a ${asignado.nombre_apellido}` : 'Libre'}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nueva terminal (PDV)"
                helperText={
                  terminalesPDV.length === 0
                    ? 'No hay terminales en modo PDV para esta empresa'
                    : `${terminalesPDV.length} terminal(es) en PDV`
                }
              />
            )}
          />

          {mismaTerminal && (
            <Alert severity="info" sx={{ mt: 2 }}>
              El chofer ya tiene asignada esa terminal.
            </Alert>
          )}

          {duenio && !mismaTerminal && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              La terminal está asignada a <strong>{duenio}</strong>. Al confirmar se le quitará y quedará
              asignada a <strong>{chofer?.nombre_apellido}</strong>.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {confirmandoQuitar && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button color="inherit" size="small" onClick={() => setConfirmandoQuitar(false)} disabled={guardando}>
                    No
                  </Button>
                  <Button color="error" size="small" variant="contained" disableElevation onClick={quitar} disabled={guardando}>
                    Sí, quitar
                  </Button>
                </Box>
              }
            >
              ¿Quitar la terminal {chofer?.posnet} a este chofer?
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mt: 3 }}>
            <Box>
              {chofer?.posnet && !confirmandoQuitar && (
                <Button color="error" onClick={() => setConfirmandoQuitar(true)} disabled={guardando}>
                  Quitar posnet
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button color="inherit" onClick={handleClose} disabled={guardando}>
                Cancelar
              </Button>
              <Button
                type="submit"
                color={duenio ? 'warning' : 'primary'}
                variant="contained"
                disableElevation
                disabled={guardando || !terminal || mismaTerminal || confirmandoQuitar}
              >
                {guardando ? 'Guardando...' : duenio ? 'Traspasar' : 'Confirmar'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default ModalAsignarPosnet;
